import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Collapse,
  Stack,
  Divider,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Badge from '@/components/common/Badge';
import { highlightText } from '@/lib/highlight';
import type { AggregatedTicker, ViewpointEntry, ReferenceItem } from '@/types';
import ReferencesModal from './ReferencesModal';

interface TickerCardProps {
  data: AggregatedTicker;
  forceTickerExpanded?: boolean;
  tickerExpandVersion?: number;
  forceSentimentExpanded?: boolean;
  sentimentExpandVersion?: number;
  disableTickerLink?: boolean;
}

// Slot order for sorting: 盘前 < 盘中 < 盘后 < null/empty
const SLOT_ORDER: Record<string, number> = {
  '盘前': 0,
  '盘中': 1,
  '盘后': 2,
};

// Sentiment section colors matching convert_csv_to_tsx.py
const SENTIMENT_COLORS = {
  bull: '#2AA84A',    // Green for bullish
  neutral: '#9AA4B2', // Gray for neutral/wait
  bear: '#FF6A00',    // Orange/red for bearish
};

interface SentimentSectionProps {
  title: string;
  entries: ViewpointEntry[];
  color: string;
  onOpenReferences: (refs: ReferenceItem[], ticker: string) => void;
  ticker: string;
  forceExpanded?: boolean;
  expandVersion?: number;
}

function SentimentSection({ title, entries, color, onOpenReferences, ticker, forceExpanded, expandVersion }: SentimentSectionProps) {
  const [expanded, setExpanded] = useState(true);

  // Respond to global toggle
  useEffect(() => {
    if (expandVersion !== undefined && expandVersion > 0) {
      setExpanded(forceExpanded ?? true);
    }
  }, [expandVersion, forceExpanded]);

  if (entries.length === 0) return null;

  // Sort entries by slot
  const sortedEntries = [...entries].sort((a, b) => {
    const orderA = SLOT_ORDER[a.slot || ''] ?? 99;
    const orderB = SLOT_ORDER[b.slot || ''] ?? 99;
    return orderA - orderB;
  });

  return (
    <Box sx={{ mt: 1 }}>
      {/* Clickable header - always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          mb: expanded ? -1.5 : 0,
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          userSelect: 'none',
          position: 'relative',
          zIndex: 1,
          ml: 1,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({entries.length})
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
            color: 'text.secondary',
          }}
        />
      </Box>

      {/* Collapsible content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Left color strip */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              bgcolor: color,
            }}
          />

          {/* Content */}
          <Box sx={{ pl: 3, pr: 2, pt: 2, pb: 2 }}>
            <Stack spacing={1.5}>
              {sortedEntries.map((entry, idx) => (
                <Box key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {/* Slot indicator */}
                    {entry.slot && (
                      <Badge variant="gray" size="small">
                        {entry.slot}
                      </Badge>
                    )}
                    {/* Author with @ */}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'primary.main', flexShrink: 0 }}
                    >
                      @{entry.author}
                    </Typography>
                    {/* Play button for references */}
                    {entry.references.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => onOpenReferences(entry.references, ticker)}
                        sx={{ p: 0.25, color: 'primary.main' }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.6, mt: 0.5, pl: entry.slot ? 0 : 0 }}
                    component="div"
                  >
                    {highlightText(entry.reasoning)}
                  </Typography>
                  {idx < sortedEntries.length - 1 && (
                    <Divider sx={{ mt: 1.5, opacity: 0.3 }} />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function TickerCard({
  data,
  forceTickerExpanded,
  tickerExpandVersion,
  forceSentimentExpanded,
  sentimentExpandVersion,
  disableTickerLink = false,
}: TickerCardProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRefs, setModalRefs] = useState<ReferenceItem[]>([]);

  const handleTickerClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger card collapse
    if (!disableTickerLink) {
      navigate(`/${data.ticker}`);
    }
  };

  // Respond to global ticker toggle
  useEffect(() => {
    if (tickerExpandVersion !== undefined && tickerExpandVersion > 0) {
      setExpanded(forceTickerExpanded ?? true);
    }
  }, [tickerExpandVersion, forceTickerExpanded]);

  const handleOpenReferences = (refs: ReferenceItem[], _ticker: string) => {
    setModalRefs(refs);
    setModalOpen(true);
  };

  const hasBull = data.bull.length > 0;
  const hasNeutral = data.neutral.length > 0;
  const hasBear = data.bear.length > 0;

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
          {/* Header - clickable to toggle */}
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: expanded ? 1 : 0,
              flexWrap: 'wrap',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {/* Expand icon */}
            <IconButton
              size="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <ExpandMoreIcon />
            </IconButton>

            {/* Ticker pill - clickable to navigate to ticker page */}
            <Box
              onClick={handleTickerClick}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 2,
                py: 0.75,
                borderRadius: 10,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'divider',
                boxShadow: 1,
                cursor: disableTickerLink ? 'default' : 'pointer',
                transition: 'all 0.2s',
                '&:hover': disableTickerLink ? {} : {
                  borderColor: 'primary.main',
                  transform: 'scale(1.02)',
                },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {data.ticker}
              </Typography>
            </Box>

            {/* Stats chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: alpha('#9AA4B2', 0.15),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: SENTIMENT_COLORS.bull,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  看涨博主 <strong>{data.bullAuthors.size}位 ({data.bullishRatio}%)</strong>
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                共 {data.authorCount} 位博主提及
              </Typography>
            </Box>
          </Box>

          {/* Collapsible sentiment sections */}
          <Collapse in={expanded}>
            {hasBull && (
              <SentimentSection
                title="【看涨】"
                entries={data.bull}
                color={SENTIMENT_COLORS.bull}
                onOpenReferences={handleOpenReferences}
                ticker={data.ticker}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
            {hasNeutral && (
              <SentimentSection
                title="【观望】"
                entries={data.neutral}
                color={SENTIMENT_COLORS.neutral}
                onOpenReferences={handleOpenReferences}
                ticker={data.ticker}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
            {hasBear && (
              <SentimentSection
                title="【看跌】"
                entries={data.bear}
                color={SENTIMENT_COLORS.bear}
                onOpenReferences={handleOpenReferences}
                ticker={data.ticker}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* References Modal */}
      <ReferencesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        references={modalRefs}
        ticker={data.ticker}
      />
    </>
  );
}
